import './Sidebar.scss';

import {Avatar, Tooltip} from '@mui/material';
import {
  BACKUPS_PATH,
  BRANCHES_PATH,
  CAMPAIGNS_PATH,
  CLIENTS_PATH,
  COMPANIES_PATH,
  COMPANY_OWNERS_PATH,
  DASHBOARD_PATH,
  DATA_COLLECTOR_PATH,
  DEALERS_PAGE_PATH,
  GET_CLIENT_TOKEN,
  LOGS_PATH,
  MANIFEST_PATH,
  REPORTS_PATH,
  STATIONS_PATH,
} from 'router/constants';
import {
  BackupIcon,
  BranchIcon,
  CampaignIcon,
  CloseIcon,
  CompaniesIcon,
  CompanyOwnerIcon,
  DatacollectorIcon,
  DealersIcon,
  LogIcon,
  ManifestIcon,
  ReportIcon,
  SidebarCollapseDoubleLeft,
  SidebarCollapseDoubleRight,
  StationIcon,
  TokenIcon,
  UsersIcon,
} from 'assets/icons';
import React, {FC, useEffect, useState} from 'react';
import store, {useAppDispatch, useAppSelector} from 'store/store';

import Button from '@mui/material/Button';
import CookieIcon from 'assets/icons/Cokkie.svg';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ThemeToggle from 'components/ThemeToggle/ThemeToggle';
import {NavLink} from 'react-router-dom';
import User from 'assets/icons/User.svg';
import {UserRole} from 'enums';
import adminHeaderAvatar from 'assets/icons/adminHeaderAvatar.svg';
import adminHeaderLogo from 'assets/icons/adminHeaderLogo.svg';
import {api} from '../../../store/slices/api';
import {avatarMenuItemStyle} from 'styles/MUIStyles/common';
import chevronDown from 'assets/icons/chevronDown.svg';
import chevronUp from 'assets/icons/chevronUp.svg';
import {clearCacheAndReload} from 'core/utils';
import {setIsAuth} from 'store/slices/authSlice';
import signOut from 'assets/icons/signOut.svg';
import {useIsMobile, useLocalStorage} from 'hooks';
import {useNavigate} from 'react-router-dom';
import {
  CampaignOutlined,
  CampaignRounded,
  SpaceDashboardOutlined,
} from '@mui/icons-material';

type MenuItemType = {
  path: string;
  icon: React.ReactNode;
  name: string;
};

const roleBasedMenuItems: Record<string, MenuItemType[]> = {
  admin: [
    {path: DEALERS_PAGE_PATH, icon: <DealersIcon />, name: 'Dealers'},
    {
      path: COMPANY_OWNERS_PATH,
      icon: <CompanyOwnerIcon />,
      name: 'Company Owners',
    },
    {path: CLIENTS_PATH, icon: <UsersIcon />, name: 'Users'},
    {path: COMPANIES_PATH, icon: <CompaniesIcon />, name: 'Companies'},
    {path: BRANCHES_PATH, icon: <BranchIcon />, name: 'Branches'},
    {path: STATIONS_PATH, icon: <StationIcon />, name: 'Stations'},
    {path: BACKUPS_PATH, icon: <BackupIcon />, name: 'Backups'},
    {path: REPORTS_PATH, icon: <ReportIcon />, name: 'Reports'},
    {path: MANIFEST_PATH, icon: <ManifestIcon />, name: 'Manifests'},
    {path: LOGS_PATH, icon: <LogIcon />, name: 'Logs & Alerts'},

    {
      path: DATA_COLLECTOR_PATH,
      icon: <DatacollectorIcon />,
      name: 'DC versions',
    },
    {
      path: CAMPAIGNS_PATH,
      icon: <CampaignIcon />,
      name: 'Campaigns',
    },
    {path: GET_CLIENT_TOKEN, icon: <TokenIcon />, name: 'Get Token'},
  ],
  dealer: [
    {
      path: COMPANY_OWNERS_PATH,
      icon: <CompanyOwnerIcon />,
      name: 'Company Owners',
    },
    {path: CLIENTS_PATH, icon: <UsersIcon />, name: 'Users'},
    {path: COMPANIES_PATH, icon: <CompaniesIcon />, name: 'Companies'},
    {path: BRANCHES_PATH, icon: <BranchIcon />, name: 'Branches'},
    {path: STATIONS_PATH, icon: <StationIcon />, name: 'Stations'},
    {path: BACKUPS_PATH, icon: <BackupIcon />, name: 'Backups'},
    {path: REPORTS_PATH, icon: <ReportIcon />, name: 'Reports'},
    {path: MANIFEST_PATH, icon: <ManifestIcon />, name: 'Manifests'},
    {path: LOGS_PATH, icon: <LogIcon />, name: 'Logs & Alerts'},
  ],
  owner: [
    {
      path: DASHBOARD_PATH,
      icon: <SpaceDashboardOutlined sx={{fontSize: 22}} />,
      name: 'Dashboard',
    },
    {path: CLIENTS_PATH, icon: <UsersIcon />, name: 'Users'},
    {path: BRANCHES_PATH, icon: <BranchIcon />, name: 'Branches'},
    {path: STATIONS_PATH, icon: <StationIcon />, name: 'Stations'},
    {path: BACKUPS_PATH, icon: <BackupIcon />, name: 'Backups'},
    {path: REPORTS_PATH, icon: <ReportIcon />, name: 'Reports'},
    {
      path: CAMPAIGNS_PATH,
      icon: <CampaignIcon />,
      name: 'Campaigns',
    },
  ],
  client: [
    {
      path: DASHBOARD_PATH,
      icon: <SpaceDashboardOutlined sx={{fontSize: 22}} />,
      name: 'Dashboard',
    },
    {path: BACKUPS_PATH, icon: <BackupIcon />, name: 'Backups'},
    {path: REPORTS_PATH, icon: <ReportIcon />, name: 'Reports'},
    {
      path: CAMPAIGNS_PATH,
      icon: <CampaignIcon />,
      name: 'Campaigns',
    },
  ],
};

const Sidebar: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) => state.auth.role);

  const [isSidebarOpen, setIsSidebarOpen] = useLocalStorage(
    'isSidebarOpen',
    false,
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isMobile = useIsMobile();
  // на телефоне сайдбар — выезжающая панель, а не постоянная колонка
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // при переходе на телефон закрываем панель, чтобы она не висела открытой
  useEffect(() => {
    if (!isMobile) setIsMobileMenuOpen(false);
  }, [isMobile]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
      return;
    }
    setIsSidebarOpen((prevState: boolean) => !prevState);
  };

  // внутри панели пункты всегда развёрнуты (с подписями)
  const isExpanded = isMobile ? true : isSidebarOpen;
  const handleSignOut = async () => {
    await clearCacheAndReload();
    store.dispatch(setIsAuth(false));
    localStorage.clear();
    dispatch(api.util.resetApiState());
  };
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  const handleProfileClick = () => {
    navigate('./profile');
    handleClose();
  };
  const clearCache = async () => {
    await clearCacheAndReload();
    window.location.href = window.location.origin + `?timestamp=${Date.now()}`;
  };
  const sidebarMenuItems = userRole
    ? roleBasedMenuItems[userRole.toLowerCase()]
    : roleBasedMenuItems.client;

  const asideClassName = [
    'sidebar',
    isExpanded ? 'active' : '',
    isMobile && isMobileMenuOpen ? 'mobileOpen' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {isMobile ? (
        <>
          <button
            type="button"
            className="sidebarBurger"
            aria-label="Open menu"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
          {isMobileMenuOpen ? (
            <div
              className="sidebarOverlay"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          ) : null}
        </>
      ) : null}

    <aside
      className={asideClassName}
      style={{
        width: isExpanded ? '256px' : '60px',
        transition: 'width 0.2s linear',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <nav
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div className="sidebarHeader">
          <img className="sidebarLogo" src={adminHeaderLogo} alt="logo" />
          {isExpanded ? (
            <span className="sidebarTitle">
              {userRole === UserRole.Admin ? 'Admin panel' : 'Backup service'}
            </span>
          ) : null}
          <button
            type="button"
            className="sidebarToggle"
            onClick={toggleSidebar}
            aria-label={
              isMobile ? 'Close menu' : isExpanded ? 'Collapse menu' : 'Expand menu'
            }
          >
            {isMobile ? (
              <CloseIcon />
            ) : isExpanded ? (
              <SidebarCollapseDoubleLeft />
            ) : (
              <SidebarCollapseDoubleRight />
            )}
          </button>
        </div>
        <ul
          className={'menuList'}
          // переход по пункту меню закрывает выезжающую панель
          onClick={() => isMobile && setIsMobileMenuOpen(false)}
        >
          {sidebarMenuItems.map((menuItem) => (
            <li key={menuItem.path} className="menuListItem">
              <NavLink to={menuItem.path} className="menuListLink">
                {/* иконка всегда в слоте фиксированного размера — иначе
                    текстовые «иконки» (например T у Get Token) съезжают */}
                {isExpanded ? (
                  <span className="menuIcon">{menuItem.icon}</span>
                ) : (
                  <Tooltip title={menuItem.name} arrow placement={'right'}>
                    <span className="menuIcon">{menuItem.icon}</span>
                  </Tooltip>
                )}
                {isExpanded ? (
                  <span className="menuLabel">{menuItem.name}</span>
                ) : null}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="avatarSideBarClosed">
          {/* переключатель темы прямо в сайдбаре, без открытия меню профиля */}
          <ThemeToggle compact={!isExpanded} />
          {isExpanded ? (
            <div className="avatar">
              <div className="avatarMenu">
                <Avatar alt="Avatar" src={adminHeaderAvatar} />
                <Button
                  id="basic-button"
                  aria-controls={open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={handleClick}
                >
                  <img src={isMenuOpen ? chevronUp : chevronDown} alt="menu" />
                </Button>
                <Menu
                  id="basic-menu2"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                >
                  <MenuItem
                    onClick={handleProfileClick}
                    sx={avatarMenuItemStyle}
                  >
                    <img src={User} alt="user" />
                    <span>My account</span>
                  </MenuItem>
                  <MenuItem onClick={handleSignOut} sx={avatarMenuItemStyle}>
                    <img src={signOut} alt="user" />
                    <span>Sign out</span>
                  </MenuItem>
                </Menu>
              </div>
              {/* <div className="menuText">
                <p>Text for subscription 25$</p>
              </div> */}
            </div>
          ) : (
            <div className="avatarPopup">
              <Button
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                <Avatar
                  alt="Avatar"
                  src={adminHeaderAvatar}
                  className="avatarClosed"
                />
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
              >
                <MenuItem onClick={clearCache} sx={avatarMenuItemStyle}>
                  <img src={CookieIcon} alt="Cookie" />
                  <span>Clear Cache</span>
                </MenuItem>
                <MenuItem onClick={handleProfileClick} sx={avatarMenuItemStyle}>
                  <img src={User} alt="user" />
                  <span>My account</span>
                </MenuItem>
                <MenuItem onClick={handleSignOut} sx={avatarMenuItemStyle}>
                  <img src={signOut} alt="user" />
                  <span>Sign out</span>
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </nav>
      </aside>
    </>
  );
};

export default Sidebar;
